<div class="page-container">
  <!-- Back nav + title -->
  <div class="page-header">
    <a class="back-link" routerLink="/orders">
      <i nz-icon nzType="arrow-left" aria-hidden="true"></i> Back to Orders
    </a>
    <div class="order-title-row" *ngIf="orderNumber">
      <h1>Order #{{ orderNumber }}</h1>
      <span *ngIf="order" class="status-badge" [ngClass]="'status-' + order.status?.toLowerCase()">
        {{ order.status }}
      </span>
    </div>
  </div>

  <nz-spin [nzSpinning]="loading">
    <div *ngIf="order" class="detail-grid">
      <!-- Main column -->
      <div class="detail-main">
        <!-- Order Items -->
        <div class="detail-card">
          <h2 class="card-title">Order Items</h2>
          <div class="table-container">
            <nz-table
              #itemsTable
              [nzData]="orderItems"
              [nzFrontPagination]="false"
              [nzShowPagination]="false"
              nzSize="middle"
            >
              <thead>
                <tr>
                  <th>Product</th>
                  <th class="text-center">Qty</th>
                  <th class="text-right">Price</th>
                  <th class="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let item of itemsTable.data">
                  <td>
                    <div class="product-cell">
                      <div class="product-thumb" *ngIf="item.productImageFileName">
                        <img
                          [src]="item.productImageFileName"
                          [alt]="item.productName"
                          width="40"
                          height="40"
                          loading="lazy"
                        />
                      </div>
                      <span class="product-name">{{ item.productName }}</span>
                    </div>
                  </td>
                  <td class="text-center tabular-nums">{{ item.quantity }}</td>
                  <td class="text-right tabular-nums">{{ formatCurrency(item.amount, order.currencyCode) }}</td>
                  <td class="text-right tabular-nums font-semibold">{{ formatCurrency(item.amount * item.quantity, order.currencyCode) }}</td>
                </tr>
              </tbody>
            </nz-table>
          </div>

          <!-- Totals -->
          <div class="totals-section">
            <div class="totals-row">
              <span class="totals-label">Subtotal</span>
              <span class="tabular-nums">{{ formatCurrency(order.amount, order.currencyCode) }}</span>
            </div>
            <div class="totals-row" *ngIf="order.processingFee">
              <span class="totals-label">Processing Fee</span>
              <span class="tabular-nums">{{ formatCurrency(order.processingFee, order.currencyCode) }}</span>
            </div>
            <div class="totals-row total">
              <span>Total</span>
              <span class="tabular-nums">{{ formatCurrency(order.amount + (order.processingFee || 0), order.currencyCode) }}</span>
            </div>
          </div>
        </div>

        <!-- Tickets -->
        <div class="detail-card">
          <h2 class="card-title">Tickets</h2>
          <div *ngIf="tickets.length > 0; else noTickets" class="tickets-grid">
            <div *ngFor="let ticket of tickets" class="ticket-card">
              <div class="ticket-header">
                <div class="ticket-number">#{{ ticket.ticketNumber }}</div>
                <span class="ticket-status" [ngClass]="'status-' + (ticket.ticketStatus || 'unknown')?.toLowerCase()">
                  {{ ticket.ticketStatus }}
                </span>
              </div>
      
              <div class="ticket-body">
                <h3 class="ticket-event-name">{{ ticket.event?.name }}</h3>
                <div class="ticket-details">
                  <div class="ticket-detail">
                    <i nz-icon nzType="calendar" aria-hidden="true" class="detail-icon"></i>
                    <span>{{ ticket.event?.date | date:'mediumDate' }}</span>
                  </div>
                  <div class="ticket-detail">
                    <i nz-icon nzType="environment" aria-hidden="true" class="detail-icon"></i>
                    <span>{{ ticket.event?.location?.description || 'Online' }}</span>
                  </div>
                  <div class="ticket-detail">
                    <i nz-icon nzType="tag" aria-hidden="true" class="detail-icon"></i>
                    <span>{{ ticket.ticketType?.name }}</span>
                  </div>
                </div>
              </div>

              <div class="ticket-body">
                <div class="info-row">
                  <span class="info-label font-semibold">Wallet Purchases</span>
                  <nz-tag *ngIf="walletTransactionsLoading" nzColor="processing">
                    Loading
                  </nz-tag>
                </div>
                <div
                  *ngIf="(walletTransactionsByTicket[ticket.uid] || []).length > 0; else noWalletTransactions"
                  class="info-list"
                >
                  <div
                    *ngFor="let transaction of walletTransactionsByTicket[ticket.uid]"
                    class="info-row"
                  >
                    <div class="info-label">
                      <span class="font-semibold">{{ transaction.merchantName }}</span><br>
                      <span>
                        {{ formatDateTime(transaction.createdDate) }}
                        <span *ngIf="transaction.notes">- {{ transaction.notes }}</span>
                      </span>
                    </div>
                    <div class="info-value">
                      <span>{{ formatCurrency(transaction.amount, transaction.currencyCode) }}</span>
                      <br>
                      <nz-tag [nzColor]="getReceiptStatusColor(transaction.receiptEmailStatus)">
                        {{ transaction.receiptEmailStatus }}
                      </nz-tag>
                    </div>
                  </div>
                </div>
                <ng-template #noWalletTransactions>
                  <p class="empty-text">
                    No merchant purchases recorded for this ticket yet.
                  </p>
                </ng-template>
              </div>
      
              <div class="ticket-footer">
                <div class="ticket-actions">
                  <button
                    nz-button
                    nzType="default"
                    nzSize="small"
                    (click)="openShareModal(ticket.uid)"
                    aria-label="Share ticket {{ ticket.ticketNumber }}"
                  >
                    <i nz-icon nzType="share-alt" aria-hidden="true"></i> Share
                  </button>
                  <a
                    nz-button
                    nzType="default"
                    nzSize="small"
                    [href]="getTicketDownloadUrl(ticket.uid)"
                    download
                    aria-label="Download ticket {{ ticket.ticketNumber }}"
                  >
                    <i nz-icon nzType="download" aria-hidden="true"></i> Download
                  </a>
                  <a
                    nz-button
                    nzType="default"
                    nzSize="small"
                    [routerLink]="['/wallet-top-up']"
                    [queryParams]="{
                      ticketUid: ticket.uid,
                      ticketNumber: ticket.ticketNumber
                    }"
                    aria-label="Top up wallet for ticket {{ ticket.ticketNumber }}"
                  >
                    <i nz-icon nzType="wallet" aria-hidden="true"></i> Top Up Wallet
                  </a>
                </div>
              </div>
            </div>
          </div>
          <ng-template #noTickets>
            <p class="empty-text">No tickets generated for this order yet.</p>
          </ng-template>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="detail-sidebar">
        <!-- Order Info -->
        <div class="detail-card">
          <h2 class="card-title">Order Information</h2>
          <div class="info-list">
            <div class="info-row">
              <span class="info-label">Order Date</span>
              <span class="info-value">{{ order.orderDate | date:'mediumDate' }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Payment</span>
              <span class="info-value">{{ order.paymentMethod }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Email</span>
              <span class="info-value email">{{ order.email }}</span>
            </div>
            <div class="info-row" *ngIf="order.contactNumber">
              <span class="info-label">Phone</span>
              <span class="info-value">{{ order.contactNumber }}</span>
            </div>
          </div>
        </div>

        <!-- Payment Info -->
        <div class="detail-card" *ngIf="$any(order).paymentTransaction">
          <h2 class="card-title">Payment Details</h2>
          <div class="info-list">
            <div class="info-row">
              <span class="info-label">Amount</span>
              <span class="info-value font-semibold">{{ formatCurrency($any(order).paymentTransaction.amount, $any(order).paymentTransaction.currencyCode) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Date</span>
              <span class="info-value">{{ $any(order).paymentTransaction.dateTimeOfPayment | date:'medium' }}</span>
            </div>
            <div class="info-row" style="align-items: center;">
              <span class="info-label">Status</span>
              <span class="info-value">
                <span class="status-badge" [ngClass]="'status-' + ($any(order).paymentTransaction.pgTransactionStatus || 'unknown')?.toLowerCase()">
                  {{ $any(order).paymentTransaction.pgTransactionStatus }}
                </span>
              </span>
            </div>
            <div class="info-row" style="flex-direction: column; align-items: flex-start; gap: 4px;">
              <span class="info-label">Reference</span>
              <span class="info-value" style="font-size: 12px; font-family: monospace; word-break: break-all; text-align: left;">{{ $any(order).paymentTransaction.referenceNumber }}</span>
            </div>
            <div class="info-row" *ngIf="$any(order).paymentTransaction.pgReferenceNumber" style="flex-direction: column; align-items: flex-start; gap: 4px;">
              <span class="info-label">Gateway Ref</span>
              <span class="info-value" style="font-size: 12px; font-family: monospace; word-break: break-all; text-align: left;">{{ $any(order).paymentTransaction.pgReferenceNumber }}</span>
            </div>
          </div>
        </div>

        <!-- Help -->
        <div class="detail-card">
          <h2 class="card-title">Need Help?</h2>
          <p class="help-text">Questions about this order? Our support team is here to help.</p>
          <a nz-button nzBlock nzType="default" href="https://about.hypenation.co.zw/contact" target="_blank" rel="noopener noreferrer">Contact Support</a>
        </div>
      </div>
    </div>
  </nz-spin>
</div>
